import { useSyncExternalStore } from "react";

// Deux réglages indépendants :
//  • le MODE  — clair, sombre, ou celui du système ;
//  • le THÈME — l'habillage (palette, boutons, champs), un fichier par thème.
// Le mode se pose en `data-theme`, le thème en `data-app-theme`, tous deux sur
// <html> : les feuilles de themes/ sont préfixées par cet attribut, elles
// peuvent donc toutes être chargées, une seule s'applique.

export const THEME_MODES = ["dark", "auto", "light"];

// Certains thèmes proposent plusieurs jeux de couleurs — la palette est un
// réglage à part du thème (comme le mode), pas un objet de boutique séparé
// par thème : une même palette pourra plus tard s'appliquer à plusieurs
// thèmes. Une entrée sans `item` est incluse gratuitement avec le thème ;
// `item` est l'objet de boutique (kind "palette") qui la déverrouille.
// `modes` : comme pour un thème, les modes que la palette tient vraiment.
export const PALETTES = {
  // Le thème de la maison prend les couleurs qu'on lui donne : c'est le même
  // dessin, la même mécanique de boutons, seules les teintes changent.
  popuquiz: [
    { id: "default",  name: "PopuQuiz",          swatch: ["#F58700", "#F8CF01"] },
    { id: "lehub",    name: "LeHUB",             swatch: ["#0E9BAE", "#AAF9FF"], item: "th_lehub" },
    { id: "valentin", name: "Saint-Valentin",    swatch: ["#FF8FB8", "#E4557F"], item: "pal_valentin" },
  ],
  // Minecraft n'a ni clair ni sombre : ses palettes sont ses dimensions.
  minecraft: [
    { id: "default", name: "Overworld", swatch: ["#5FA83D", "#8B5A2B"] },
    { id: "nether",  name: "Nether",    swatch: ["#6B3131", "#F0A728"], item: "pal_mc_nether" },
    { id: "end",     name: "End",       swatch: ["#6B4C68", "#C3C894"], item: "pal_mc_end" },
  ],
  // Splatoon : les deux encres de chaque jeu. Le premier vient avec le thème,
  // les deux suivants s'achètent.
  splatoon: [
    { id: "default", name: "Splatoon 1", swatch: ["#FF6A13", "#3A30E8"] },
    { id: "s2",      name: "Splatoon 2", swatch: ["#F72D8C", "#5CF23A"], item: "pal_spl_2" },
    { id: "s3",      name: "Splatoon 3", swatch: ["#E4F53A", "#5534E6"], item: "pal_spl_3" },
  ],
  neon: [
    { id: "default", name: "Orange & Citron vert", swatch: ["#FF7A18", "#C7FF3D"] },
    { id: "rose",    name: "Rose & Cyan",           swatch: ["#FF3FA4", "#4DE0FF"], item: "pal_neon_rose" },
    { id: "cyan",    name: "Cyan & Rouge",          swatch: ["#7DF9FF", "#FF2E4C"], item: "pal_neon_cyan" },
    { id: "vert",    name: "Vert & Jaune",          swatch: ["#6BFF9E", "#FFD447"], item: "pal_neon_vert" },
  ],
};

// `item` : l'objet de boutique qui déverrouille l'habillage. Sans lui, le thème
// est offert à tout le monde (les deux PopuQuiz, qui sont la maison).
// `modes` : les modes d'affichage que l'habillage sait vraiment tenir. Absent =
// les deux ; vide = aucun, l'habillage a une seule ambiance (Minecraft). Un thème qui n'a qu'une ambiance (Néon est une enseigne de nuit)
// grise les autres choix plutôt que de s'afficher à moitié dessiné.
export const THEMES = [
  { id: "popuquiz", name: "PopuQuiz", logo: "/logo512.png" },
  { id: "popuwatch",   name: "Popuwatch", item: "th_popuwatch" },
  { id: "neon",        name: "Néon",      item: "th_neon", modes: ["dark"] },
  { id: "frutiger",    name: "Frutiger Aero", item: "th_frutiger" },
  { id: "gundam",      name: "Gundam",    item: "th_gundam" },
  // Pas en boutique : trouve la porte qui apparaît une heure par nuit (voir
  // components/BackroomsDoor.js). `secret` fait dire « ? » aux Réglages plutôt
  // qu'un prix.
  { id: "backrooms",   name: "Backrooms", item: "th_backrooms", secret: true },
  { id: "seventies",   name: "Seventies", item: "th_seventies" },
  { id: "eighties",    name: "Eighties", item: "th_80s" },
  { id: "noel",        name: "Noël",     item: "th_noel" },
  { id: "piscine",     name: "Piscine",  item: "th_piscine" },
  { id: "minecraft",   name: "Minecraft", item: "th_minecraft", modes: [] },
  { id: "stardew",     name: "Stardew Valley", item: "th_stardew" },
  { id: "splatoon",    name: "Splatoon", item: "th_splatoon" },
  { id: "crochet",     name: "Crochet",  item: "th_crochet" },
];
const THEME_BY_ID = new Map(THEMES.map(th => [th.id, th]));

// Les modes tenus par un habillage, ou par sa palette quand elle est plus
// restrictive. « auto » n'est proposé que si les deux le sont : sinon il
// promettrait un basculement qui n'arriverait jamais.
export function themeModes(themeId, paletteId) {
  const only = PALETTES[themeId]?.find(p => p.id === paletteId)?.modes
    || THEME_BY_ID.get(themeId)?.modes;
  if (!only || only.length > 1) return THEME_MODES;
  return only;
}

const THEME_IDS = THEMES.map(t => t.id);
const DEFAULT_THEME = "popuquiz";
const DEFAULT_MODE = "dark";

const MODE_KEY = "popuquiz_theme_mode";
const THEME_KEY = "popuquiz_theme";
const PALETTE_KEY = "popuquiz_theme_palettes"; // { [themeId]: paletteId }

const media = typeof window !== "undefined" && window.matchMedia
  ? window.matchMedia("(prefers-color-scheme: light)")
  : null;

function readMode() {
  const v = localStorage.getItem(MODE_KEY);
  return THEME_MODES.includes(v) ? v : DEFAULT_MODE;
}
// Les deux habillages PopuQuiz n'en font plus qu'un : une préférence enregistrée
// sur l'ancien nom retombe sur le thème unique, sans que personne ne le remarque.
const RETIRED_THEMES = { "popuquiz-v1": "popuquiz", "popuquiz-v2": "popuquiz", "lehub": "popuquiz" };
// LeHUB n'est plus un habillage mais une palette du thème de la maison : qui
// l'avait choisi le retrouve, à la bonne couleur, sans rien avoir à refaire.
const RETIRED_AS_PALETTE = { lehub: { theme: "popuquiz", palette: "lehub" } };
function readTheme() {
  const raw = localStorage.getItem(THEME_KEY);
  const v = RETIRED_THEMES[raw] || raw;
  return THEME_IDS.includes(v) ? v : DEFAULT_THEME;
}
// Le mode « automatique » n'existe pas en CSS : on le résout ici.
export function resolveMode(mode) {
  if (mode !== "auto") return mode;
  return media && media.matches ? "light" : "dark";
}
// Le mode réellement posé sur la page : celui demandé, sauf si l'habillage ne
// le tient pas — auquel cas c'est le sien qui gagne. Le réglage de l'utilisateur
// n'est pas écrasé pour autant : il reprend effet dès qu'il change de thème.
export function effectiveMode(mode, themeId, paletteId) {
  const only = themeModes(themeId, paletteId);
  const wanted = resolveMode(mode);
  return only.includes(wanted) ? wanted : only.find(m => m !== "auto") || wanted;
}
function readPalettes() {
  let raw;
  try {
    raw = JSON.parse(localStorage.getItem(PALETTE_KEY) || "{}");
    if (!raw || typeof raw !== "object") raw = {};
  } catch (_) { raw = {}; }
  // Habillage devenu palette : on reporte le choix une bonne fois pour toutes.
  const moved = RETIRED_AS_PALETTE[localStorage.getItem(THEME_KEY)];
  if (moved && !raw[moved.theme]) {
    raw[moved.theme] = moved.palette;
    try { localStorage.setItem(PALETTE_KEY, JSON.stringify(raw)); } catch (_) {}
  }
  return raw;
}
// La palette active pour un thème : celle mémorisée si elle existe encore
// dans son catalogue, sinon la première — toujours gratuite, jamais un choix
// qui laisserait l'écran sans couleurs. La possession (payante ou non) est
// vérifiée en amont, dans les réglages — ce module ne fait qu'appliquer.
export function resolvePalette(themeId, palettes) {
  const list = PALETTES[themeId];
  if (!list || !list.length) return null;
  const wanted = palettes[themeId];
  return list.find(p => p.id === wanted)?.id || list[0].id;
}

const subs = new Set();
let state = { mode: readMode(), theme: readTheme(), palettes: readPalettes() };
function snapshot() { return state; }
function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }

function apply() {
  const root = document.documentElement;
  const palette = resolvePalette(state.theme, state.palettes);
  root.setAttribute("data-theme", effectiveMode(state.mode, state.theme, palette));
  root.setAttribute("data-app-theme", state.theme);
  if (palette) root.setAttribute("data-app-palette", palette);
  else root.removeAttribute("data-app-palette");
  subs.forEach(fn => fn());
}

export function setThemeMode(mode) {
  if (!THEME_MODES.includes(mode)) return;
  state = { ...state, mode };
  localStorage.setItem(MODE_KEY, mode);
  apply();
}
export function setTheme(theme) {
  if (!THEME_IDS.includes(theme)) return;
  state = { ...state, theme };
  localStorage.setItem(THEME_KEY, theme);
  apply();
}
export function setPalette(themeId, paletteId) {
  if (!PALETTES[themeId]?.some(p => p.id === paletteId)) return;
  const palettes = { ...state.palettes, [themeId]: paletteId };
  state = { ...state, palettes };
  localStorage.setItem(PALETTE_KEY, JSON.stringify(palettes));
  apply();
}
export function useThemeState() {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

// Appliqué dès le chargement du module, avant le premier rendu, pour éviter le
// flash d'un thème qui n'est pas celui choisi.
apply();
// Le système change d'avis (nuit qui tombe, réglage de l'OS) : on suit, mais
// seulement en mode automatique.
if (media) {
  const onChange = () => { if (state.mode === "auto") apply(); };
  media.addEventListener ? media.addEventListener("change", onChange) : media.addListener(onChange);
}
